/**
 * NFMCheckoutButton - Initiates Stripe Checkout via NFM
 * 
 * IMPLEMENTS THE 8-STEP PURCHASE FLOW:
 * Step 6: Engagement Level Gate - checks engagement before checkout
 * Step 7: Billing Profile Check - verifies payment method exists
 * Step 8: Monetization Action - creates checkout session
 * 
 * This component handles the complete checkout flow:
 * 1. Validates user is authenticated
 * 2. CHECKS engagement level is set (Phase III-B Gate)
 * 3. CHECKS billing profile status (Phase IV-A Gate)
 * 4. Creates checkout session via NFM API
 * 5. Redirects to Stripe Checkout
 * 
 * SECURITY: Amount determined server-side from catalog.
 * For bundles, is_subscriber flag determines which price to use.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { initiateNFMCheckout, checkEngagementRequired, getEngagementStatus } from '@/services/nfmService';
import { getBillingProfileStatus } from '@/services/billingService';
import EngagementLevelModal from './EngagementLevelModal';

export default function NFMCheckoutButton({
  productType,    // 'subscription' or 'bundle'
  productId,      // Plan or bundle ID from catalog
  isSubscriber,   // For bundle pricing: true = subscriber price, false = a la carte price
  children,       // Button content
  className,
  disabled,
  requireAuth = true,
  ...buttonProps
}) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  
  // Determine subscriber status from user or prop
  const userIsSubscriber = isSubscriber !== undefined 
    ? isSubscriber 
    : user?.membership_type === 'Subscriber';
  
  const handleCheckout = async () => {
    // Check authentication if required
    if (requireAuth && !user) {
      toast.info('Please sign in to continue');
      navigate('/login');
      return;
    }
    
    setLoading(true);
    
    try {
      // STEP 6: Check if engagement level is set (Phase III-B Gate)
      console.log('[NFM] Step 6: Checking engagement level gate...');
      const engagementCheck = await checkEngagementRequired(user?.id, token);
      
      if (engagementCheck.requires_engagement_selection) {
        // Show the engagement level gate modal
        console.log('[NFM] Engagement level required - showing gate modal');
        setLoading(false);
        setShowEngagementModal(true);
        return;
      }
      
      // STEP 7: Check billing profile status (Phase IV-A Gate)
      console.log('[NFM] Step 7: Checking billing profile...');
      try {
        const billingStatus = await getBillingProfileStatus(user?.id, token);
        if (!billingStatus.has_payment_method) {
          console.log('[NFM] No payment method on file - will collect during Stripe checkout');
          // Stripe Checkout will handle card collection, so we proceed
        }
      } catch (billingError) {
        console.warn('[NFM] Billing check failed, proceeding anyway:', billingError);
        // Non-blocking - continue to checkout
      }
      
      // STEP 8: Proceed with checkout
      console.log('[NFM] Step 8: Proceeding to checkout');
      await proceedToCheckout();
      
    } catch (error) {
      console.error('[NFM] Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };
  
  const proceedToCheckout = async () => {
    try {
      // Get engagement status to determine subscriber pricing
      let effectiveIsSubscriber = userIsSubscriber;
      try {
        const engagementStatus = await getEngagementStatus(user?.id, token);
        effectiveIsSubscriber = engagementStatus.is_subscriber || userIsSubscriber;
        console.log('[NFM] Engagement status:', engagementStatus, 'Using subscriber pricing:', effectiveIsSubscriber);
      } catch (e) {
        console.warn('[NFM] Could not get engagement status, using default:', effectiveIsSubscriber);
      }
      
      await initiateNFMCheckout({
        productType,
        productId,
        isSubscriber: effectiveIsSubscriber,
        userId: user?.id,
        token
      });
      // Note: If successful, user is redirected to Stripe.
      // This code won't execute after redirect.
    } catch (error) {
      console.error('[NFM] Checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };
  
  const handleEngagementSuccess = async (result) => {
    // Engagement level has been set - now proceed with checkout
    setLoading(true);
    await proceedToCheckout();
  };
  
  return (
    <>
      <Button
        onClick={handleCheckout}
        disabled={disabled || loading}
        className={className}
        data-testid={`nfm-checkout-${productType}-${productId}`}
        {...buttonProps}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          children
        )}
      </Button>
      
      {/* Phase III-B: Engagement Level Gate Modal */}
      <EngagementLevelModal
        isOpen={showEngagementModal}
        onClose={() => setShowEngagementModal(false)}
        onSuccess={handleEngagementSuccess}
        userId={user?.id}
        token={token}
      />
    </>
  );
}
